// Assistant action executor (spec §3.8, §4, §9 C5/C6): every catalog action maps to an endpoint wrapper the screens
// already use, so it runs with the user's own session, role checks, idempotency and audit — the assistant never gets a
// second write path. Every execution is claimed on the server first (one run per message and index, across tabs),
// then run, then reported; the report is display text only and never re-enters the model.
import { api, isApiError } from "../../api/client.ts";
import { changeExecution, getProjectDetail, getTask, taskAction, updateProject, validateProject, deleteProject } from "../../api/endpoints.ts";
import {
  activateRoutingConfig,
  getConnection,
  getModelTarget,
  listRoutingConfigs,
  probeModelTarget,
  sendProjectMessage,
  updateAgentProfile,
  updateConnection,
  updateModelTarget,
} from "../../api/endpoints-wave2.ts";
import { getIntegration, integrationStateAction } from "../../api/endpoints-wave3.ts";
import { decideApproval, startPlanningRun } from "../../api/endpoints-wave5.ts";
import { requestGuardianAction, runGuardian } from "../../api/endpoints-guardian.ts";
import { requestTaskTranslation } from "../../api/endpoints-content-language.ts";
import {
  claimAssistantAction,
  getAgentProfileVersion,
  getRoutingConfigScope,
  reportAssistantAction,
  setExecutionPolicy,
  startTaskRun,
  updateTaskText,
} from "../../api/endpoints-assistant.ts";
import { createProjectFromBrief, publishProject } from "../../api/endpoints-microsystem.ts";
import { ASSISTANT_ACTION_CATALOG, type AssistantTurnMeta } from "@maestros/contracts";
import type { AssistantAction, AssistantClaimVia, AssistantReply } from "../../api/types-assistant.ts";
import { newId } from "../../lib/storage.ts";
import { waitForOperation } from "../../pages/project/ExecutionControls.tsx";
import { serverRunsAction } from "./operator-loop.ts";

export interface ActionEnv {
  navigate: (to: string) => void;
  currentPath?: string;
  handoff?: { messageId: string; index: number };
}

export interface ActionResult {
  outcome: "succeeded" | "failed";
  /**
   * A token the panel translates (`assistant.detail.<token>`), never server text (R29): `ready` / `not_ready` /
   * `still_running` on success, the API error code (or `check_failed`) on failure; null when there is nothing to add.
   */
  detail: string | null;
  refs?: AssistantReply["refs"];
}

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** The project a path belongs to (`/projects/<uuid>/…`), or null for any other page. */
export function projectIdOf(pathname: string): string | null {
  const id = /^\/projects\/([^/]+)/.exec(pathname)?.[1] ?? null;
  return id !== null && UUID.test(id) ? id : null;
}

/**
 * Refusal causes specific enough to say more than the error code (D28 from-brief: 409 with `details.cause`; D34 task
 * runs and execution: `project_not_active`, `not_ready`, `at_capacity`, `task_not_startable`, `invalid_transition`).
 */
const DETAIL_CAUSES = new Set(["github_unavailable", "repository_exists", "no_system_project", "project_not_active", "not_ready", "at_capacity", "task_not_startable", "invalid_transition", "execution_policy_not_settable"]);

function errorDetail(error: unknown): string | null {
  if (!isApiError(error)) return null;
  const cause = error.details?.["cause"];
  return typeof cause === "string" && DETAIL_CAUSES.has(cause) ? cause : error.code;
}

/** Types that move the user into a form or a connection flow: never run by themselves, whatever a reply says. */
const NEVER_AUTO = new Set<AssistantAction["type"]>(["open_settings_form", "connect_repository", "create_project_from_brief", "decide_approval"]);

/**
 * Whether an auto action may run by itself right now (D34: every auto-tier type the server stamped `auto`). In a Home
 * session only the catalog's `homeAuto` organization actions (D36: a MaestrOS task; the domain stamps them auto only in
 * autonomous mode) — never a navigation or anything else there (R18/R32); never one refused for the user's role (shown
 * as refused); never a form/connection/approval flow; and a navigation only to a page of the session's own project
 * (R31: leaving the scope would swap the conversation) while the user is still on the page the question was asked from
 * (Review Focus 2). Anything else stays a card and waits for a click. On a turn the server executor runs (D41-B2) the
 * server's own set is never runnable here.
 */
export function autoRunnable(action: AssistantAction, at: { sessionProjectId: string | null; currentPath: string; turnRoute: string | null; turn?: AssistantTurnMeta | null }): boolean {
  if (action.mode !== "auto" || action.refused !== undefined || NEVER_AUTO.has(action.type)) return false;
  if (serverRunsAction(action, at.turn, at.sessionProjectId === null)) return false;
  if (at.sessionProjectId === null) return ASSISTANT_ACTION_CATALOG[action.type].homeAuto === true;
  if (action.type === "open_page") return projectIdOf(action.args.path) === at.sessionProjectId && at.turnRoute !== null && at.currentPath === at.turnRoute;
  return true;
}

/** A refusal decided here before any call (a token, like the API error codes). */
class Refused extends Error {
  readonly token: string;
  constructor(token: string) {
    super(token);
    this.token = token;
  }
}

interface StoredWorkspace {
  runner_id: string;
  operating_system: "linux" | "macos" | "windows";
  root_path: string;
  subpath: string;
  commands: Record<string, unknown>;
}

/**
 * `update_project_settings`: the project fields, plus the first workspace's commands/subpath through the same
 * workspace list PATCH /v1/projects accepts. The stored execution policy is never sent (the API refuses it, D10, and
 * carries it over by workspace identity).
 */
async function updateSettings(args: Extract<AssistantAction, { type: "update_project_settings" }>["args"]): Promise<void> {
  const project = (await getProjectDetail(args.project_id)) as Awaited<ReturnType<typeof getProjectDetail>> & { workspaces?: StoredWorkspace[] };
  const body: Record<string, unknown> = { expected_version: project.version };
  for (const key of ["name", "description", "repository_url", "base_branch"] as const) if (args[key] !== undefined) body[key] = args[key];
  if (args.test_command !== undefined || args.build_command !== undefined || args.workspace_subpath !== undefined) {
    const workspaces = project.workspaces ?? [];
    if (workspaces.length === 0) throw new Refused("no_workspace");
    body["workspaces"] = workspaces.map((w, i) => {
      const { execution_policy: _kept, ...commands } = w.commands;
      if (i > 0) return { runner_id: w.runner_id, operating_system: w.operating_system, root_path: w.root_path, subpath: w.subpath, commands };
      return {
        runner_id: w.runner_id,
        operating_system: w.operating_system,
        root_path: w.root_path,
        subpath: args.workspace_subpath ?? w.subpath,
        commands: { ...commands, ...(args.test_command !== undefined ? { test: args.test_command } : {}), ...(args.build_command !== undefined ? { build: args.build_command } : {}) },
      };
    });
  }
  await updateProject(args.project_id, body as Parameters<typeof updateProject>[1]);
}

async function execute(action: AssistantAction, env: ActionEnv): Promise<string | null> {
  switch (action.type) {
    case "validate_project": {
      const accepted = await validateProject(action.args.project_id);
      const op = await waitForOperation(accepted.operation_id, () => true);
      if (op === null || op.status === "accepted" || op.status === "running") return "still_running";
      if (op.status !== "succeeded") throw new CheckFailed();
      const failed = (op.result?.checks ?? []).some((c) => c.status !== "passed" && c.status !== "not_applicable");
      return failed ? "not_ready" : "ready";
    }
    case "open_page":
      env.navigate(action.args.path);
      return null;
    case "open_settings_form":
      env.navigate(`/projects/${encodeURIComponent(action.args.project_id)}/settings?field=${action.args.field}`);
      return null;
    case "connect_repository":
      env.navigate(`/projects/${encodeURIComponent(action.args.project_id)}/settings?connect=1`);
      return null;
    case "retry_task":
    case "unblock_task":
    case "resource_available": {
      const verb = action.type === "retry_task" ? "retry" : action.type === "unblock_task" ? "unblock" : "resource_available";
      await taskAction(action.args.task_id, { action: verb, expected_version: action.args.expected_version });
      return null;
    }
    case "start_project":
    case "resume_project": {
      const project = await getProjectDetail(action.args.project_id);
      await changeExecution(project.id, action.type === "start_project" ? "start" : "resume", project.version);
      return null;
    }
    case "create_task":
    case "create_project_request":
    case "create_system_task":
      throw new Refused("not_found"); // A handoff requires a claimed, persisted reply; runAction handles it below.
    case "add_guidance": {
      const task = await getTask(action.args.task_id);
      await sendProjectMessage(task.project_id, { content: action.args.text, command: { type: "add_guidance", task_id: action.args.task_id, text: action.args.text } }, newId());
      return null;
    }
    case "decide_approval":
      await decideApproval(action.args.approval_id, { option: action.args.option });
      return null;
    case "create_project_from_brief": {
      // D28: the API creates the project, its private repository, the clone and a planning run; the Kanban page then
      // shows the launch card (provision → planning → start agents → publish). One key per click: the claim already
      // guarantees a single run of this action, the key only covers a retried request.
      const created = await createProjectFromBrief({ name: action.args.name, brief: action.args.brief }, newId());
      if (env.currentPath !== "/" && !env.currentPath?.startsWith("/chats/")) env.navigate(`/projects/${encodeURIComponent(created.project.id)}/kanban`);
      return "project_created";
    }
    // ---------- D34 ----------
    case "update_project_settings":
      await updateSettings(action.args);
      return null;
    case "set_execution_policy":
      await setExecutionPolicy(action.args.project_id, action.args.enabled);
      return action.args.enabled ? "policy_on" : "policy_off";
    case "edit_task": {
      const { task_id, ...fields } = action.args;
      const task = await getTask(task_id);
      await updateTaskText(task_id, { expected_version: task.version, ...fields });
      return null;
    }
    case "move_task":
    case "cancel_task": {
      const task = await getTask(action.args.task_id);
      const verb = action.type === "cancel_task" ? "cancel" : action.args.action;
      await taskAction(action.args.task_id, { action: verb, expected_version: task.version, ...(action.args.reason ? { reason: action.args.reason } : {}) });
      return null;
    }
    case "start_task_run": {
      const run = await startTaskRun(action.args.task_id);
      return run.reason_code === "already_running" ? "already_running" : "task_started";
    }
    case "pause_project":
    case "drain_project": {
      const project = await getProjectDetail(action.args.project_id);
      const reason = action.type === "pause_project" ? action.args.reason : undefined;
      await changeExecution(project.id, action.type === "pause_project" ? "pause" : "drain", project.version, reason);
      return null;
    }
    case "run_model_probe":
      await probeModelTarget(action.args.model_target_id, "capability", action.args.max_cost_cents);
      return "still_running";
    case "update_agent_profile": {
      const { agent_profile_id, ...fields } = action.args;
      const profile = await getAgentProfileVersion(agent_profile_id);
      await updateAgentProfile(agent_profile_id, { expected_version: profile.version, ...fields } as Parameters<typeof updateAgentProfile>[1]);
      return null;
    }
    case "update_model_target": {
      const target = await getModelTarget(action.args.model_target_id);
      await updateModelTarget(action.args.model_target_id, { expected_version: target.version, state: action.args.state });
      return null;
    }
    case "update_provider_connection": {
      const connection = await getConnection(action.args.provider_connection_id);
      await updateConnection(action.args.provider_connection_id, { expected_version: connection.version, state: action.args.state });
      return null;
    }
    case "activate_integration": {
      const integration = await getIntegration(action.args.integration_id);
      await integrationStateAction(action.args.integration_id, "activate", { expected_version: integration.version });
      return null;
    }
    case "activate_routing_config": {
      // The API takes the version active in the configuration's own scope (0 when none) to refuse a concurrent change.
      const config = await getRoutingConfigScope(action.args.routing_config_id);
      const scope = (await listRoutingConfigs(config.project_id)).items.filter((c) => c.project_id === config.project_id && c.state === "active");
      await activateRoutingConfig(config.id, scope[0]?.version ?? 0);
      return null;
    }
    case "publish_project":
      await publishProject(action.args.project_id);
      return "publish_started";
    // ---------- D36 ----------
    case "start_planning_run":
      await startPlanningRun(action.args.project_id);
      return "planning_started";
    case "run_guardian":
      await runGuardian();
      return "guardian_queued";
    case "review_learnings":
      await api("/learning-governor/run", { method: "POST", body: action.args });
      return "learning_review_queued";
    case "server_operation":
      await api("/server-operations", { method: "POST", body: action.args, idempotencyKey: newId() });
      return "server_operation_queued";
    case "github_operation": {
      const response = await api<{ operation: { state: string } }>("/github-operations", { method: "POST", body: action.args, idempotencyKey: newId() });
      if (response.operation.state !== "succeeded") throw new Refused(response.operation.state === "failed" ? "github_request_refused" : "github_outcome_requires_reconciliation");
      return "github_request_completed";
    }
    case "guardian_incident_action":
      await requestGuardianAction(action.args.incident_id, action.args.action);
      return "guardian_action_requested";
    // ---------- D37.3 ----------
    case "delete_project":
      // RF-DEL-01: the card's click is the confirmation (it showed the name and task counts); the API refuses running work.
      await deleteProject(action.args.project_id);
      return "project_deleted";
    case "translate_tasks": {
      // The card's click is the consent for a free model; a paid planner model asks again on the Kanban (needs_confirmation).
      const r = await requestTaskTranslation(action.args.project_id);
      return r.run === null ? "nothing_to_translate" : "translation_queued";
    }
  }
}

/** The readiness validation ran and failed (the operation's own message is server text, never shown). */
class CheckFailed extends Error {}

/** Runs one catalog action through the existing endpoint wrappers; a refusal is a `failed` result, never thrown. */
export async function runAction(action: AssistantAction, env: ActionEnv): Promise<ActionResult> {
  try {
    if (action.type === "create_task" || action.type === "create_system_task" || action.type === "create_project_request") {
      if (action.type === "create_system_task" && !action.args.project_id) throw new Refused("no_system_project");
      if (!env.handoff) throw new Refused("not_found");
      const { messageId, index } = env.handoff;
      const result = await api<ActionResult>(`/assistant/actions/${encodeURIComponent(messageId)}/${index}/handoff`, { method: "POST", body: {} });
      if (result.outcome !== "succeeded" || !result.refs?.length || result.refs.some(ref => !UUID.test(ref.id))) throw new Refused("check_failed");
      return { outcome: result.outcome, detail: result.detail, ...(result.refs ? { refs: result.refs } : {}) };
    }
    return { outcome: "succeeded", detail: await execute(action, env) };
  } catch (error) {
    return { outcome: "failed", detail: error instanceof CheckFailed ? "check_failed" : error instanceof Refused ? error.token : errorDetail(error) };
  }
}

/**
 * Claim → run → report (§9 C6). `already_claimed` (409): another tab or an earlier click ran it — nothing happens here.
 * A claim refused for any other reason is shown as a failure and the action is not run.
 */
export async function claimAndRun(messageId: string, index: number, action: AssistantAction, env: ActionEnv, via: AssistantClaimVia): Promise<ActionResult | "already_claimed"> {
  try {
    await claimAssistantAction(messageId, index, via);
  } catch (error) {
    if (isApiError(error) && error.status === 409) return "already_claimed";
    return { outcome: "failed", detail: errorDetail(error) };
  }
  const result = await runAction(action, { ...env, handoff: { messageId, index } });
  // The action already ran: a lost report only leaves the server ledger at "pending"; the result is still shown here.
  await reportAssistantAction(messageId, index, { outcome: result.outcome, ...(result.detail ? { detail: result.detail } : {}) }).catch(() => undefined);
  return result;
}
